/**
 * JoinTeam - Page d'acceptation d'invitation d'équipe
 * Affiche les infos de l'invitation et permet d'accepter
 */

import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, AlertCircle, Loader, Crown, ArrowRight, LogIn } from 'lucide-react';
import config from '../../config';

function JoinTeam({ token, invitationToken, onAccepted, onNeedLogin }) {
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Valider le token d'invitation au chargement
  useEffect(() => {
    const validateInvitation = async () => {
      if (!invitationToken) {
        setError('Token d\'invitation manquant');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${config.API_URL}/api/invitations/validate/${invitationToken}`);
        const data = await response.json();

        if (data.success) {
          setInvitationInfo(data);
        } else {
          // Supprimer le token pending car l'invitation n'est plus valide
          localStorage.removeItem('pendingInvitationToken');
          setError(data.error || 'Invitation invalide');
        }
      } catch (err) {
        // En cas d'erreur réseau, supprimer aussi le token pour éviter les boucles
        localStorage.removeItem('pendingInvitationToken');
        setError('Erreur lors de la validation de l\'invitation');
      } finally {
        setLoading(false);
      }
    };

    validateInvitation();
  }, [invitationToken]);

  // Accepter l'invitation
  const handleAcceptInvitation = async () => {
    if (!token) {
      // Stocker le token pour après le login
      localStorage.setItem('pendingInvitationToken', invitationToken);
      onNeedLogin();
      return;
    }

    setAccepting(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/api/invitations/accept/${invitationToken}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        // Supprimer le token pending
        localStorage.removeItem('pendingInvitationToken');
        // Callback pour recharger l'app
        setTimeout(() => {
          onAccepted(data);
        }, 2000);
      } else {
        setError(data.error || 'Erreur lors de l\'acceptation');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setAccepting(false);
    }
  };

  // État de chargement
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={40} className="spin" style={{ color: '#1a1a1a', marginBottom: '16px' }} />
          <p style={{ color: '#6b7280' }}>Validation de l'invitation...</p>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .spin { animation: spin 1s linear infinite; }
          `}</style>
        </div>
      </div>
    );
  }

  // Erreur
  if (error && !invitationInfo) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <AlertCircle size={32} color="#dc2626" />
          </div>
          <h1 style={{ margin: '0 0 12px', fontSize: '22px', color: '#1f2937' }}>
            Invitation invalide
          </h1>
          <p style={{ margin: '0 0 24px', color: '#6b7280', lineHeight: '1.6' }}>
            {error}
          </p>
          <a
            href="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: '#1a1a1a',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600'
            }}
          >
            Retour à l'accueil
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    );
  }

  // Succès
  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#f0fdf4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <CheckCircle size={32} color="#16a34a" />
          </div>
          <h1 style={{ margin: '0 0 12px', fontSize: '22px', color: '#1f2937' }}>
            Bienvenue dans l'équipe !
          </h1>
          <p style={{ margin: '0 0 24px', color: '#6b7280', lineHeight: '1.6' }}>
            Vous avez rejoint l'équipe de {invitationInfo?.inviter?.name || invitationInfo?.inviter?.email}.
            Vous bénéficiez maintenant de son plan et avez accès aux données partagées.
          </p>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>
            Redirection en cours...
          </p>
        </div>
      </div>
    );
  }

  // Page d'acceptation
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8fafc',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '450px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '40px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            backgroundColor: '#FAF9F5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Users size={36} color="#1a1a1a" />
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: '24px', color: '#1f2937' }}>
            Invitation à rejoindre une équipe
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            sur Kokpit
          </p>
        </div>

        {/* Inviter info */}
        <div style={{
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {invitationInfo?.inviter?.picture ? (
              <img
                src={invitationInfo.inviter.picture}
                alt={invitationInfo.inviter.name}
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Crown size={28} color="white" />
              </div>
            )}
            <div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                {invitationInfo?.inviter?.name || 'Un utilisateur'}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                {invitationInfo?.inviter?.email}
              </div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>
                vous invite à rejoindre son équipe
              </div>
            </div>
          </div>
        </div>

        {/* Avantages */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '14px', color: '#374151', fontWeight: '600' }}>
            En rejoignant cette équipe, vous bénéficierez :
          </h3>
          <ul style={{
            margin: 0,
            padding: '0 0 0 20px',
            color: '#6b7280',
            lineHeight: '1.8',
            fontSize: '14px'
          }}>
            <li>Du même plan d'abonnement que le propriétaire</li>
            <li>De l'accès aux connecteurs partagés</li>
            <li>De l'accès aux sites et données partagées</li>
            <li>De toutes les fonctionnalités Kokpit</li>
          </ul>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Action buttons */}
        {token ? (
          <button
            onClick={handleAcceptInvitation}
            disabled={accepting}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '14px 24px',
              backgroundColor: accepting ? '#9ca3af' : '#1a1a1a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: accepting ? 'not-allowed' : 'pointer'
            }}
          >
            {accepting ? (
              <>
                <Loader size={18} className="spin" />
                Acceptation en cours...
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                Accepter l'invitation
              </>
            )}
          </button>
        ) : (
          <div>
            <p style={{
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '14px',
              marginBottom: '16px'
            }}>
              Connectez-vous pour accepter cette invitation
            </p>
            <button
              onClick={() => {
                localStorage.setItem('pendingInvitationToken', invitationToken);
                onNeedLogin();
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '14px 24px',
                backgroundColor: '#1a1a1a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <LogIn size={18} />
              Se connecter
            </button>
          </div>
        )}

        {/* Email hint */}
        <p style={{
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: '13px',
          marginTop: '16px'
        }}>
          Cette invitation a été envoyée à <strong>{invitationInfo?.email}</strong>
        </p>
      </div>
    </div>
  );
}

export default JoinTeam;
